import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';

import { SearchComponent } from '@/components/common/Search';
import DisplayNoResult from '@/components/common/DisplayNoResult';

import type { RefuelDataGridRow } from '../../types/RefuelDataGridRow.types';
import { createRefuelColumns } from './config/refuelDataGridColumns';
import { createRefuelDataGridLocaleText, REFUEL_DATA_GRID_OPTIONS } from './config/refuelDataGridOptions';
import { createRefuelDataGridSlotProps } from './config/refuelDataGridSlotProps';
import { REFUELS_LIST_VIEW_COLUMN } from './RefuelDataGridListViewCell';
import { RefuelDataGridToolbar } from './RefuelDataGridToolbar';

type RefuelDataGridProps = {
  rows: RefuelDataGridRow[];
  isListView: boolean;
  isFetching: boolean;
  exportFileName: string;
};

function matchesRefuelSearch(row: RefuelDataGridRow, searchText: string): boolean {
  const normalizedSearchText = searchText.toLowerCase();
  const visibleValues = [row.refuelDate, row.refuelPlace, row.startFuelLevel, row.amountRefuels, row.stopFuelLevel];

  return visibleValues.some((value) => value.toString().toLowerCase().includes(normalizedSearchText));
}

function RefuelDataNoRows() {
  return <DisplayNoResult />;
}

export function RefuelDataGrid({ rows, isListView, isFetching, exportFileName }: RefuelDataGridProps) {
  const { t, i18n } = useTranslation();
  const [searchText, setSearchText] = useState<string>();

  const columns = useMemo(() => createRefuelColumns(t), [t]);
  const slotProps = createRefuelDataGridSlotProps(exportFileName);

  const visibleRows = searchText ? rows.filter((row) => matchesRefuelSearch(row, searchText)) : rows;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      <SearchComponent
        searchText={searchText}
        setSearchText={setSearchText}
        placeholder={t('refuels.fuelSearchPlaceHolder')}
      />
      <DataGridPro
        {...REFUEL_DATA_GRID_OPTIONS}
        rows={visibleRows}
        columns={columns}
        autoHeight={isListView}
        listView={isListView}
        listViewColumn={REFUELS_LIST_VIEW_COLUMN}
        hideFooterRowCount={isFetching || isListView}
        disableRowSelectionOnClick
        disableColumnMenu
        disableDensitySelector
        localeText={createRefuelDataGridLocaleText(t, i18n.language)}
        slots={{
          toolbar: RefuelDataGridToolbar,
          noRowsOverlay: RefuelDataNoRows,
        }}
        sx={{
          fontSize: (theme) => theme.typography.pxToRem(15),
          '& .MuiDataGrid-cell': { pt: 1.25 },
          '& .MuiDataGrid-main': { minHeight: '300px' },
        }}
        loading={isFetching}
        slotProps={slotProps}
        showToolbar
      />
    </Box>
  );
}
